import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { ExpandLess, ChevronRight } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import { ActionMenu } from 'components/_atoms';
import { error, neutral } from '_lex/_foundations/phxColors';
import { TemplateSection, TemplateField, Template } from '../_types';
import { filterActiveSections, filterActiveFields } from '../_utils/templateFilters';

interface TemplateOutlineCardProps {
  template: Template;
  onEditClick: () => void;
  actionLabel?: string;
  canManage: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

const TemplateOutlineCard: React.FC<TemplateOutlineCardProps> = ({
  template,
  onEditClick,
  actionLabel,
  canManage,
  isLoading = false,
  isError = false,
}) => {
  // Session storage-based collapse state (similar to template builder but with proper initial state)
  const storageKey = `phx-template-outline-collapsed-${template?.id || 'unknown'}`;
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  React.useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(collapsedSections));
    } catch (err) {
      console.error('Failed to save collapse state to sessionStorage', err);
    }
  }, [storageKey, collapsedSections]);

  React.useEffect(() => {
    if (template?.templateSections && template.templateSections.length > 0) {
      // Check sessionStorage directly to avoid dependency on collapsedSections state
      try {
        const stored = sessionStorage.getItem(storageKey);
        const hasStoredState = stored && JSON.parse(stored) && Object.keys(JSON.parse(stored)).length > 0;

        if (!hasStoredState) {
          const initialCollapsedState: Record<string, boolean> = {};
          template.templateSections.forEach((section, index) => {
            if (index > 0) {
              initialCollapsedState[section.sectionGuid] = true;
            }
          });
          setCollapsedSections(initialCollapsedState);
        }
      } catch (err) {
        console.error('Failed to read collapse state from sessionStorage', err);
      }
    }
  }, [template?.id, storageKey]);

  const toggleSection = (sectionGuid: string) => {
    setCollapsedSections((prev) => {
      const next = { ...prev };
      if (next[sectionGuid]) {
        delete next[sectionGuid];
      } else {
        next[sectionGuid] = true;
      }
      return next;
    });
  };

  const renderField = (field: TemplateField, index: number) => (
    <ListItem
      key={field.id}
      sx={{
        py: 2.5,
        px: 3,
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant='bodyMd'
              sx={{
                color: neutral[600],
                minWidth: '20px',
              }}
            >
              {index + 1}.
            </Typography>
            <Typography
              variant='bodyMd'
              sx={{
                fontWeight: 500,
                color: neutral[900],
              }}
            >
              {field.effectiveLabel || field.name}
            </Typography>
            <PhxBadge
              color='default'
              size='sm'
            >
              {field.fieldType}
            </PhxBadge>
          </Box>
        }
        sx={{ margin: 0 }}
      />
    </ListItem>
  );

  const renderSection = (section: TemplateSection) => {
    const isCollapsed = collapsedSections[section.sectionGuid] || false;
    const isExpanded = !isCollapsed;
    // Filter out deleted fields
    const activeFields = filterActiveFields(section.sectionFields || []);
    const hasFields = activeFields.length > 0;

    const handleSectionClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleSection(section.sectionGuid);
    };

    return (
      <Box
        key={section.id}
        sx={{
          border: `1px solid ${neutral[200]}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Section Header */}
        <Box
          onClick={handleSectionClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 3,
            backgroundColor: neutral[50],
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: neutral[100],
            },
          }}
        >
          <IconButton
            size='small'
            sx={{
              p: 0,
              color: neutral[600],
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={handleSectionClick}
          >
            {isExpanded ? <ExpandLess /> : <ChevronRight />}
          </IconButton>
          <Typography
            variant='subtitleMd'
            sx={{
              fontWeight: 600,
              color: neutral[900],
            }}
          >
            {section.effectiveName || section.name}
          </Typography>
        </Box>

        {/* Section Content */}
        <Collapse in={isExpanded}>
          <Box sx={{ backgroundColor: '#ffffff', p: 4 }}>
            {hasFields ? (
              <List sx={{ py: 0 }}>
                {activeFields
                  .sort((a: TemplateField, b: TemplateField) => (a.order || 0) - (b.order || 0))
                  .map((field, index) => renderField(field, index))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography
                  variant='bodySm'
                  sx={{ color: neutral[600] }}
                >
                  {t('performanceReporting.templates.details.outline.noFields')}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !template) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: error[600] }}>{t('performanceReporting.templates.details.outline.error')}</Typography>
      </Box>
    );
  }

  // Filter out deleted sections
  const sections = filterActiveSections(template.templateSections || []);

  return (
    <Stack spacing={12}>
      <Card className='lex-card'>
        <CardHeader
          title={t('performanceReporting.templates.details.outline.title')}
          action={
            canManage ? (
              <ActionMenu
                id='template-outline-actions'
                btnText={t('performanceReporting.templates.details.actions.title')}
                btnProps={{ disabled: isLoading }}
                items={[
                  {
                    key: 'edit-template',
                    label: actionLabel || t('performanceReporting.templates.details.actions.editTemplate'),
                    onClick: onEditClick,
                  },
                ]}
              />
            ) : null
          }
        />
        <CardContent>
          {/* Template Sections */}
          {sections.length > 0 ? (
            <Stack
              spacing={4}
              sx={{ px: 4 }}
            >
              {sections.map(renderSection)}
            </Stack>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography
                variant='bodySm'
                sx={{ color: neutral[600] }}
              >
                {t('performanceReporting.templates.details.outline.noSections')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TemplateOutlineCard;

